'use client';

// imports
import { Lightbulb, Sun, Eye, Coffee, Wind, CheckCircle2, AlertTriangle } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';

// data
const tips = [
  {
    icon: Sun,
    title: 'Use good lighting',
    description: 'Natural daylight or a bright, white light source gives the clearest tongue color reading.',
    type: 'do',
  },
  {
    icon: Eye,
    title: 'Tongue fully visible',
    description: 'Stick your tongue out fully. The entire dorsal surface should be visible with no teeth blocking the view.',
    type: 'do',
  },
  {
    icon: Wind,
    title: 'Avoid blur',
    description: 'Hold the camera steady. Use a tripod or prop your hand against a surface to prevent motion blur.',
    type: 'do',
  },
  {
    icon: Coffee,
    title: 'Avoid colored drinks',
    description: 'Do not eat, drink colored beverages, or brush your tongue for at least 30 minutes before scanning.',
    type: 'avoid',
  },
];

const checklist = [
  { label: 'Natural or white light', ok: true },
  { label: 'Tongue fully extended', ok: true },
  { label: 'Camera in focus', ok: true },
  { label: 'No colored food/drink recently', ok: true },
  { label: 'No lipstick or lip products', ok: true },
  { label: 'Neutral background', ok: true },
];

// component
export default function UploadTips({ tipsRef, highlightTips,}: {
  tipsRef: React.RefObject<HTMLDivElement | null>;
  highlightTips: boolean;}) {

  return (
    <div className="w-full lg:w-80 xl:w-96 flex-shrink-0 h-full flex flex-col">

    {/* container */}
    <div className="flex flex-col h-full gap-7">

        {/* tips card */}
        <div
          ref={tipsRef}
          className={`
            bg-white rounded-3xl shadow-soft border overflow-hidden
            transition-all duration-500
            ${highlightTips
              ? 'border-blue-400 ring-2 ring-blue-200 scale-[1.02]'
              : 'border-slate-100'
            }
          `}
        >
          <div className="px-6 pt-6 pb-4 border-b border-slate-50">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center">
                <Lightbulb size={16} className="text-amber-500" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-base">Photography Tips</h3>
                <p className="text-xs text-slate-400">For better analysis accuracy</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4 flex-1">
            {tips?.map((tip) => {
              const Icon = tip?.icon;
              return (
                <div key={tip?.title} className="flex gap-3">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    tip?.type === 'do' ? 'bg-emerald-50' : 'bg-amber-50'
                  }`}>
                    <Icon size={14} className={tip?.type === 'do' ? 'text-emerald-500' : 'text-amber-500'} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-semibold text-slate-700">{tip?.title}</p>
                      <span className={`text-xs rounded-full px-1.5 py-0.5 font-medium ${
                        tip?.type === 'do'
                          ? 'bg-emerald-50 text-emerald-600' :'bg-amber-50 text-amber-600'
                      }`}>
                        {tip?.type === 'do' ? '✓ Do' : '✗ Avoid'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{tip?.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* checklist card */}
        <div className="bg-white rounded-3xl shadow-soft border border-slate-100 p-6">
          <h3 className="font-bold text-slate-800 text-base mb-4 flex items-center gap-2">
            <CheckCircle2 size={16} className="text-blue-500" />
            Pre-scan Checklist
          </h3>
          <div className="space-y-2.5">
            {checklist?.map((item) => (
              <div key={item?.label} className="flex items-center gap-3">
                <div className="w-5 h-5 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={13} className="text-emerald-500" />
                </div>
                <span className="text-sm text-slate-600">{item?.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* disclaimer card */}
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-800 font-semibold text-sm mb-1">Medical Disclaimer</p>
              <p className="text-amber-700 text-xs leading-relaxed">
                Insighta AI provides a preliminary risk assessment only. Results are not a medical diagnosis and should not replace consultation with a licensed healthcare professional.
              </p>
            </div>
          </div>
        </div>

    </div>
    </div>
  );
}